import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Sun, Moon, User, LogOut, Trash2, ChevronRight, MapPin, Trophy, Bell, BellOff, MessageSquare, Star, Shield, HelpCircle, FileText, Mail, ExternalLink, Ban } from 'lucide-react';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import { TIER_STYLES, TierPill } from '../components/TierBadge';
import FeedbackPopup from '../components/FeedbackPopup';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Blocked users list — rendered as an expandable row inside Settings.
const BlockedUsersLink = () => {
  const [open, setOpen] = useState(false);
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/moderation/blocked`);
      setBlocked(r.data.blocked || []);
    } catch { toast.error('Failed to load blocked users'); }
    setLoading(false);
  };

  const toggle = async () => {
    if (!open) await load();
    setOpen(!open);
  };

  const unblock = async (u) => {
    if (!window.confirm(`Unblock ${u.display_name || u.username}?`)) return;
    try {
      await axios.delete(`${API}/moderation/block/${u.id}`);
      setBlocked(blocked.filter(b => b.id !== u.id));
      toast.success('Unblocked');
    } catch { toast.error('Failed to unblock'); }
  };

  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors w-full text-left"
        data-testid="settings-blocked-users-toggle"
      >
        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
          <Ban className="w-5 h-5 text-muted-foreground" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-sm">Blocked Users</p>
          <p className="text-xs text-muted-foreground">
            {loading ? 'Loading…' : blocked.length === 0 ? "You haven't blocked anyone" : `${blocked.length} blocked`}
          </p>
        </div>
        <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="pl-14 pr-2 pb-2 space-y-1" data-testid="settings-blocked-users-list">
          {blocked.length === 0 && (
            <p className="text-xs text-muted-foreground py-2">No blocked users. When you block someone from their profile, they show up here.</p>
          )}
          {blocked.map(u => (
            <div key={u.id} className="flex items-center gap-2 py-1.5" data-testid={`blocked-user-${u.id}`}>
              {u.profile_photo ? (
                <img src={u.profile_photo} alt="" className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary">
                  {(u.display_name || u.username || '?')[0]?.toUpperCase()}
                </div>
              )}
              <span className="text-sm text-foreground flex-1 truncate">{u.display_name || u.username}</span>
              <button
                type="button"
                onClick={() => unblock(u)}
                className="text-xs text-primary hover:underline"
                data-testid={`unblock-btn-${u.id}`}
              >
                Unblock
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Settings = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [calcHandicap, setCalcHandicap] = useState(null);

  // Fetch calculated handicap
  useEffect(() => {
    axios.get(`${API}/golf/calculated-handicap`).then(r => {
      if (r.data?.handicap_index != null) setCalcHandicap(r.data);
    }).catch(() => {});
  }, []);

  // Check notification status on mount
  useEffect(() => {
    const checkNotificationStatus = async () => {
      try {
        const response = await axios.get(`${API}/notifications/status`);
        setNotificationsEnabled(response.data.enabled);
      } catch (error) {
        /* silent */
      }
    };
    checkNotificationStatus();
  }, []);

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    if (photoPath.startsWith('http')) return photoPath;
    // Handle both /uploads/ (old) and /api/photos/ (new) paths
    if (photoPath.startsWith('/uploads/')) {
      const filename = photoPath.replace('/uploads/', '');
      return `${BACKEND_URL}/api/photos/${filename}`;
    }
    return `${BACKEND_URL}${photoPath}`;
  };

  const getDefaultPhoto = (name) => {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'User')}&size=200&background=076652&color=fff&font-size=0.4`;
  };

  const handleToggleNotifications = async () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      toast.error('Your browser does not support push notifications');
      return;
    }

    setNotificationsLoading(true);

    try {
      if (!notificationsEnabled) {
        // Request permission
        const permission = await Notification.requestPermission();
        
        if (permission === 'granted') {
          // Register service worker
          const registration = await navigator.serviceWorker.register('/sw.js');
          await navigator.serviceWorker.ready;

          // Get VAPID public key from server
          const vapidRes = await axios.get(`${API}/notifications/vapid-key`);
          const vapidPublicKey = vapidRes.data.publicKey;

          // Convert base64 to Uint8Array
          const padding = '='.repeat((4 - vapidPublicKey.length % 4) % 4);
          const base64 = (vapidPublicKey + padding).replace(/-/g, '+').replace(/_/g, '/');
          const rawData = atob(base64);
          const outputArray = new Uint8Array(rawData.length);
          for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);

          // Subscribe via PushManager
          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: outputArray,
          });

          // Send subscription to backend
          await axios.post(`${API}/notifications/subscribe`, subscription.toJSON());
          setNotificationsEnabled(true);
          toast.success('Notifications enabled!');
        } else {
          toast.error('Please allow notifications in your browser settings');
        }
      } else {
        // Unsubscribe
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          const subscription = await registration.pushManager.getSubscription();
          if (subscription) await subscription.unsubscribe();
        }
        await axios.post(`${API}/notifications/unsubscribe`);
        setNotificationsEnabled(false);
        toast.success('Notifications disabled');
      }
    } catch (error) {
      toast.error('Notification setup failed');
      toast.error('Failed to update notification settings');
    } finally {
      setNotificationsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/account`);
      logout();
      toast.success('Account deleted');
      navigate('/');
    } catch (error) {
      toast.error('Failed to delete account');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-32">
      <Navigation />

      {/* Header */}
      <div className="px-4 pt-6 pb-4 max-w-lg mx-auto flex items-center gap-3">
        <img src="/logo-the-club.png" alt="The Club" className="h-7 w-auto" />
        <h1 className="font-serif text-2xl font-bold" style={{ color: 'var(--deep-forest)' }}>Settings</h1>
      </div>

      <div className="max-w-lg mx-auto px-4 space-y-6">
        {/* Profile Card */}
        <Card className="rounded-2xl border-border/50 overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <Avatar className="w-20 h-20 border-4 border-primary/20">
                <AvatarImage
                  src={getPhotoUrl(user?.profile_photo) || getDefaultPhoto(user?.display_name)}
                  alt={user?.display_name}
                />
                <AvatarFallback className="bg-primary text-primary-foreground text-xl font-semibold">
                  {user?.display_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1">
                <h2 className="font-serif text-xl font-semibold">
                  {user?.display_name || user?.username}
                </h2>
                <p className="text-muted-foreground text-sm">@{user?.username}</p>
                <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                  {user?.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {user.location}
                    </span>
                  )}
                  {user?.handicap && (
                    <span className="flex items-center gap-1">
                      <Trophy className="w-3 h-3" />
                      {user.handicap} Hcp
                    </span>
                  )}
                </div>
                {calcHandicap && (
                  <div className="mt-2 px-3 py-1.5 rounded-lg bg-primary/10 inline-flex items-center gap-2" data-testid="calculated-handicap">
                    <Trophy className="w-3.5 h-3.5 text-primary" />
                    <span className="text-sm font-bold text-primary">{calcHandicap.handicap_index}</span>
                    <span className="text-[10px] text-muted-foreground">Calculated Index</span>
                    <span className="text-[10px] text-muted-foreground">({calcHandicap.message})</span>
                  </div>
                )}
              </div>
            </div>

            <Button
              variant="outline"
              onClick={() => navigate('/profile/edit')}
              className="w-full mt-4 rounded-xl h-12"
              data-testid="edit-profile-btn"
            >
              <User className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card className="rounded-2xl border-border/50">
          <CardHeader>
            <CardTitle className="font-serif text-lg">Notifications</CardTitle>
            <CardDescription>Get notified about matches and messages</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
              <div className="flex items-center gap-3">
                {notificationsEnabled ? (
                  <Bell className="w-5 h-5 text-primary" />
                ) : (
                  <BellOff className="w-5 h-5 text-muted-foreground" />
                )}
                <div>
                  <Label className="text-base">Push Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    {notificationsEnabled ? 'You\'ll receive alerts' : 'Enable to stay updated'}
                  </p>
                </div>
              </div>
              <Switch
                checked={notificationsEnabled}
                onCheckedChange={handleToggleNotifications}
                disabled={notificationsLoading}
                data-testid="notifications-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card className="rounded-2xl border-border/50">
          <CardHeader>
            <CardTitle className="font-serif text-lg">Appearance</CardTitle>
            <CardDescription>Customize how the app looks</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
              <div className="flex items-center gap-3">
                {theme === 'dark' ? (
                  <Moon className="w-5 h-5 text-secondary" />
                ) : (
                  <Sun className="w-5 h-5 text-primary" />
                )}
                <div>
                  <Label className="text-base">Dark Mode</Label>
                  <p className="text-sm text-muted-foreground">
                    {theme === 'dark' ? 'Clubhouse Evening' : 'Clubhouse Morning'}
                  </p>
                </div>
              </div>
              <Switch
                checked={theme === 'dark'}
                onCheckedChange={toggleTheme}
                data-testid="theme-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* App Guide */}
        <Card className="rounded-2xl border-border/50">
          <CardContent className="p-0">
            <button
              onClick={() => {
                localStorage.removeItem('club_onboarding_done');
                navigate('/social');
                window.location.reload();
              }}
              className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-muted/50 transition-colors"
              data-testid="app-guide-btn"
            >
              <div className="flex items-center gap-3">
                <HelpCircle className="w-5 h-5 text-muted-foreground" />
                <div>
                  <span className="font-medium">App Guide</span>
                  <p className="text-xs text-muted-foreground">Review how The Club works</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>
          </CardContent>
        </Card>

        {/* Account */}
        <Card className="rounded-2xl border-border/50">
          <CardHeader>
            <CardTitle className="font-serif text-lg">Account</CardTitle>
            <CardDescription>Manage your account settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-muted/50 transition-colors"
              data-testid="logout-btn"
            >
              <div className="flex items-center gap-3">
                <LogOut className="w-5 h-5 text-muted-foreground" />
                <span>Log Out</span>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>
          </CardContent>
        </Card>

        {/* Founder card */}
        {user?.is_founder && (
          <Card className="rounded-2xl border shadow-club-sm overflow-hidden" style={{ borderColor: '#D4AF5A' }} data-testid="founder-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: TIER_STYLES.founder.ring }}>
                  <span style={{ fontSize: 14, fontWeight: 900, color: TIER_STYLES.founder.text }}>TC</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm">Founder of The Club</p>
                    <TierPill tier="founder" size="xs" />
                  </div>
                  <p className="text-xs text-muted-foreground">Member #{user.user_number || 1} · Every new member follows you automatically, and anyone who likes your profile matches instantly.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Platinum Badge Status */}
        {user?.is_platinum && !user?.is_founder && (
          <Card className="rounded-2xl border shadow-club-sm overflow-hidden" style={{ borderColor: 'var(--fairway-sand)' }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: TIER_STYLES.platinum.ring }}>
                  <span style={{ fontSize: 14, fontWeight: 900, color: TIER_STYLES.platinum.text }}>TC</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm">Platinum Founding Member</p>
                    <TierPill tier="platinum" size="xs" />
                  </div>
                  <p className="text-xs text-muted-foreground">You're one of our first members. Lifetime access — no subscription ever.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Feedback Section */}
        <Card className="rounded-2xl border shadow-club-sm overflow-hidden" style={{ borderColor: 'var(--fairway-sand)' }}>
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-lg">Feedback</CardTitle>
            <CardDescription>Help us improve The Club</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <button
              onClick={() => setShowFeedback(true)}
              className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors hover:bg-muted"
              data-testid="settings-feedback-btn"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-muted">
                <MessageSquare className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm">Send Feedback</p>
                <p className="text-xs text-muted-foreground">Rate your experience and suggest improvements</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
          </CardContent>
        </Card>

        {/* Version */}
        <p className="text-center text-sm text-muted-foreground py-4">
          The Club Golf v1.0.0
        </p>

        {/* Legal & Support */}
        <Card className="rounded-2xl border-border/50" data-testid="settings-legal-card">
          <CardHeader>
            <CardTitle className="text-base font-serif">Legal &amp; Support</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <BlockedUsersLink />
            <a
              href="/privacy.html"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors w-full text-left"
              data-testid="settings-privacy-link"
            >
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <Shield className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm">Privacy Policy</p>
                <p className="text-xs text-muted-foreground">How we collect, use, and protect your data</p>
              </div>
              <ExternalLink className="w-4 h-4 text-muted-foreground" />
            </a>
            <a
              href="/terms.html"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors w-full text-left"
              data-testid="settings-terms-link"
            >
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <FileText className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm">Terms of Service</p>
                <p className="text-xs text-muted-foreground">The rules of using The Club Golf</p>
              </div>
              <ExternalLink className="w-4 h-4 text-muted-foreground" />
            </a>
            <a
              href="/contact.html"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors w-full text-left"
              data-testid="settings-contact-link"
            >
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <Mail className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm">Contact &amp; Support</p>
                <p className="text-xs text-muted-foreground">Email us at support@theclubgolf.co</p>
              </div>
              <ExternalLink className="w-4 h-4 text-muted-foreground" />
            </a>
            <a
              href="/delete-account.html"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors w-full text-left"
              data-testid="settings-delete-account-info-link"
            >
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm">Account Deletion Info</p>
                <p className="text-xs text-muted-foreground">How to delete your account and data</p>
              </div>
              <ExternalLink className="w-4 h-4 text-muted-foreground" />
            </a>
          </CardContent>
        </Card>

        {/* Attribution */}
        <p className="text-center text-[11px] text-muted-foreground/70 pt-4">
          The Club Golf is owned and operated by Clubhouse Labs, LLC.
        </p>

        {/* Delete Account — intentionally buried */}
        <div className="pt-8 pb-4">
          <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <DialogTrigger asChild>
              <button
                className="w-full text-center text-xs text-muted-foreground/60 hover:text-destructive transition-colors py-2"
                data-testid="delete-account-btn"
              >
                Delete Account
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader>
                <DialogTitle className="font-serif">Delete Account?</DialogTitle>
                <DialogDescription>
                  This action cannot be undone. All your data, matches, and messages will be permanently deleted.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleteDialogOpen(false)}
                  className="rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteAccount}
                  disabled={deleting}
                  className="rounded-xl"
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? 'Deleting...' : 'Delete Account'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      {showFeedback && <FeedbackPopup context="settings" onClose={() => setShowFeedback(false)} />}
    </div>
  );
};

export default Settings;
